import {
  ALL_CONTACT_TYPES,
  AU_STATE_CODES,
  PROJECT_TYPES,
  PROJECT_TYPE_LABELS,
  resolveContactTypes,
  type AuStateCode,
  type ContactFilter,
  type ProjectType,
} from '@deltasuite/zoho-client';
import { useMemo, useState } from 'react';

export interface FilterPanelProps {
  value: FilterPanelValue;
  onChange: (next: FilterPanelValue) => void;
}

export interface FilterPanelValue {
  states: AuStateCode[];
  projectTypes: ProjectType[];
  advancedTypes: string[];
  hasMobile: boolean;
  lastActivityAfter: string | null;
  lastActivityBefore: string | null;
}

export const EMPTY_FILTER: FilterPanelValue = {
  states: [],
  projectTypes: [],
  advancedTypes: [],
  hasMobile: false,
  lastActivityAfter: null,
  lastActivityBefore: null,
};

export function toContactFilter(v: FilterPanelValue): ContactFilter {
  const companyType = resolveContactTypes({
    projectTypes: v.projectTypes,
    advanced: v.advancedTypes,
  });
  return {
    state: v.states.length > 0 ? v.states : undefined,
    companyType: companyType.length > 0 ? companyType : undefined,
    hasMobile: v.hasMobile || undefined,
    lastActivityAfter: v.lastActivityAfter ? `${v.lastActivityAfter}T00:00:00Z` : undefined,
    lastActivityBefore: v.lastActivityBefore ? `${v.lastActivityBefore}T23:59:59Z` : undefined,
  };
}

function toggle<T>(arr: readonly T[], v: T): T[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-full border px-3 py-1.5 font-sans text-xs transition-colors ' +
        (active
          ? 'border-signal-amber bg-signal-amber/10 text-signal-amber'
          : 'border-graphite-300 bg-surface text-graphite-700 hover:bg-surface-sunk')
      }
      aria-pressed={active}
    >
      {children}
    </button>
  );
}

export function FilterPanel({ value, onChange }: FilterPanelProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const advancedAvailable = useMemo(
    () => ALL_CONTACT_TYPES.filter((t) => !value.advancedTypes.includes(t)),
    [value.advancedTypes],
  );

  return (
    <section className="flex flex-col gap-8">
      <div>
        <h3 className="font-sans text-xs font-medium uppercase tracking-[0.18em] text-graphite-500">
          State
        </h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {AU_STATE_CODES.map((code) => (
            <Chip
              key={code}
              active={value.states.includes(code)}
              onClick={() => onChange({ ...value, states: toggle(value.states, code) })}
            >
              {code}
            </Chip>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-sans text-xs font-medium uppercase tracking-[0.18em] text-graphite-500">
          Project type
        </h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {PROJECT_TYPES.map((t) => (
            <Chip
              key={t}
              active={value.projectTypes.includes(t)}
              onClick={() => onChange({ ...value, projectTypes: toggle(value.projectTypes, t) })}
            >
              {PROJECT_TYPE_LABELS[t]}
            </Chip>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setAdvancedOpen((o) => !o)}
          className="mt-3 font-mono text-[0.7rem] uppercase tracking-wider text-graphite-500 hover:text-graphite-900"
        >
          {advancedOpen ? '— hide advanced' : '+ advanced'}
        </button>
        {advancedOpen ? (
          <div className="mt-3 space-y-2">
            {value.advancedTypes.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {value.advancedTypes.map((t) => (
                  <Chip
                    key={t}
                    active
                    onClick={() =>
                      onChange({
                        ...value,
                        advancedTypes: value.advancedTypes.filter((x) => x !== t),
                      })
                    }
                  >
                    {t} ×
                  </Chip>
                ))}
              </div>
            ) : null}
            <select
              className="w-full rounded-md border border-graphite-300 bg-surface px-3 py-2 font-sans text-sm text-graphite-900"
              value=""
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                onChange({ ...value, advancedTypes: [...value.advancedTypes, v] });
              }}
            >
              <option value="">add raw picklist value…</option>
              {advancedAvailable.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      <div>
        <h3 className="font-sans text-xs font-medium uppercase tracking-[0.18em] text-graphite-500">
          Other
        </h3>
        <label className="mt-3 flex items-center gap-2 font-sans text-sm text-graphite-700">
          <input
            type="checkbox"
            checked={value.hasMobile}
            onChange={(e) => onChange({ ...value, hasMobile: e.target.checked })}
            className="size-4 accent-signal-amber"
          />
          Has mobile number
        </label>
      </div>

      <div>
        <h3 className="font-sans text-xs font-medium uppercase tracking-[0.18em] text-graphite-500">
          Last activity
        </h3>
        <div className="mt-3 flex gap-3">
          <label className="flex flex-1 flex-col gap-1 font-mono text-xs text-graphite-500">
            after
            <input
              type="date"
              value={value.lastActivityAfter ?? ''}
              onChange={(e) => onChange({ ...value, lastActivityAfter: e.target.value || null })}
              className="rounded-md border border-graphite-300 bg-surface px-3 py-2 font-sans text-sm text-graphite-900"
            />
          </label>
          <label className="flex flex-1 flex-col gap-1 font-mono text-xs text-graphite-500">
            before
            <input
              type="date"
              value={value.lastActivityBefore ?? ''}
              onChange={(e) => onChange({ ...value, lastActivityBefore: e.target.value || null })}
              className="rounded-md border border-graphite-300 bg-surface px-3 py-2 font-sans text-sm text-graphite-900"
            />
          </label>
        </div>
      </div>
    </section>
  );
}
